import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { DollarSign, Target, Eye, Edit, Trash2, Ship, FolderPlus, Gavel, MoreHorizontal } from 'lucide-react';
import { formatCurrency } from '../crmUtils';
import { PRIORIDADE_CONFIG, ORIGEM_ICONES } from './constants';

export const OportunidadeCard = ({ oportunidade, index, origens, onView, onEdit, onDelete }) => {
  const OrigemIcon = ORIGEM_ICONES[oportunidade.origem] || MoreHorizontal;
  const prioridadeConfig = PRIORIDADE_CONFIG[oportunidade.prioridade] || PRIORIDADE_CONFIG.media;

  return (
    <Draggable key={oportunidade.id} draggableId={oportunidade.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`bg-white rounded-lg border shadow-sm p-3 mb-2 cursor-grab active:cursor-grabbing transition-shadow ${
            snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400' : 'hover:shadow-md'
          }`}
          data-testid={`oportunidade-card-${oportunidade.id}`}
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="font-medium text-sm text-gray-900 line-clamp-2 flex-1">{oportunidade.titulo}</h4>
            <Badge className={`text-[10px] shrink-0 ${prioridadeConfig.cor}`} title={`Prioridade ${prioridadeConfig.label}`}>
              {prioridadeConfig.label}
            </Badge>
          </div>

          <div className="text-xs text-gray-500 mb-2 truncate">
            {oportunidade.cliente_nome || oportunidade.contato_nome || 'Sem cliente'}
          </div>

          <div className="flex items-center gap-3 mb-2">
            <div className="flex items-center gap-1 text-xs font-medium text-green-600">
              <DollarSign className="h-3 w-3" />
              {formatCurrency(oportunidade.valor_estimado)}
            </div>
            {oportunidade.etapa !== 'concluido' && oportunidade.etapa !== 'descartado' && (
              <div className="flex items-center gap-1 text-xs text-gray-500" title="Probabilidade baseada no Lead Scoring">
                <Target className="h-3 w-3" />
                {oportunidade.probabilidade || 0}%
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-1 mb-2">
            {oportunidade.embarcacoes?.length > 0 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                <Ship className="h-2.5 w-2.5 mr-0.5" />
                {oportunidade.embarcacoes.reduce((acc, e) => acc + (e.quantidade || 1), 0)} emb.
              </Badge>
            )}
            {oportunidade.origem === 'licitacao' && oportunidade.licitacao_numero_processo && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                <Gavel className="h-2.5 w-2.5 mr-0.5" />
                {oportunidade.licitacao_numero_processo.slice(0, 10)}...
              </Badge>
            )}
            {oportunidade.projeto_criado_id && (
              <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700">
                <FolderPlus className="h-2.5 w-2.5 mr-0.5" />
                Projeto
              </Badge>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <div className="flex items-center gap-2">
              <OrigemIcon className="h-3.5 w-3.5 text-gray-400" title={origens.find(o => o.id === oportunidade.origem)?.nome} />
              {oportunidade.responsavel_nome && (
                <span className="text-[10px] text-gray-500 truncate max-w-[60px]" title={oportunidade.responsavel_nome}>
                  {oportunidade.responsavel_nome.split(' ')[0]}
                </span>
              )}
            </div>
            <div className="flex gap-0.5">
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onView(oportunidade)} title="Ver detalhes">
                <Eye className="h-3 w-3 text-gray-400" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onEdit(oportunidade)} title="Editar">
                <Edit className="h-3 w-3 text-gray-400" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDelete(oportunidade.id)} title="Excluir">
                <Trash2 className="h-3 w-3 text-red-400" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};
